// 地圖 / 平面圖頁的資料（platform vmsmaps.go）：範圍內各家戶與組織的平面圖、戶外擺放、家戶座標、場域座標、使用者偏好。
import { useMemo } from 'react';
import { useQueries, useQuery } from '@tanstack/react-query';
import { useScope } from '../../lib/scope';
import { useDevices } from '../../lib/useDevices';
import { orgApi, ownerKey, vmsApi, type ApiFloorplan, type ApiPlacement, type FloorplanOwner, type HomeGeo, type Site, type UserPrefs } from '../../lib/planeApi';

export interface OwnedFloorplan extends ApiFloorplan {
  owner: FloorplanOwner;
  canWrite: boolean;
}

/** 範圍內的平面圖擁有者：每個家戶 + 範圍內的組織。 */
export function useFloorplanOwners(): FloorplanOwner[] {
  const { results } = useDevices();
  const { org, orgs, scope } = useScope();
  return useMemo(() => {
    const out: FloorplanOwner[] = results.map((r) => ({ kind: 'home' as const, api: r.home.endpoints.api, homeId: r.home.home_id, label: r.home.display_name || r.home.home_id }));
    const os = scope.startsWith('home:') ? [] : org ? [org] : orgs;
    for (const o of os) out.push({ kind: 'org', api: o.api, orgId: o.org_id, label: o.display_name });
    return out;
  }, [results, org, orgs, scope]);
}

export function useFloorplans(owners: FloorplanOwner[]) {
  const qs = useQueries({
    queries: owners.map((o) => ({
      queryKey: ['floorplans', ownerKey(o)],
      queryFn: () => vmsApi.floorplans(o),
      staleTime: 10_000,
      refetchInterval: 30_000,
      retry: false,
    })),
  });
  const list: OwnedFloorplan[] = useMemo(
    () => owners.flatMap((o, i) => (qs[i]?.data?.data ?? []).map((f) => ({ ...f, owner: o, canWrite: !!qs[i]?.data?.can_write }))),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [owners, qs.map((q) => q.dataUpdatedAt).join(',')],
  );
  const writable = owners.filter((_, i) => qs[i]?.data?.can_write);
  return { list, writable, loading: qs.some((q) => q.isLoading) };
}

/** 平面圖檔的簽名網址（5 分鐘有效；4 分鐘換一次）。 */
export function useFloorplanImage(fp: OwnedFloorplan | undefined) {
  return useQuery({
    queryKey: ['fp-img', fp?.floorplan_id, fp?.version],
    enabled: !!fp,
    staleTime: 4 * 60_000,
    refetchInterval: 4 * 60_000,
    queryFn: () => vmsApi.floorplanImage(fp!.owner, fp!.floorplan_id).then((r) => r.url),
  });
}

export function useFloorplanImages(fps: OwnedFloorplan[]) {
  const qs = useQueries({
    queries: fps.map((fp) => ({
      queryKey: ['fp-img', fp.floorplan_id, fp.version],
      staleTime: 4 * 60_000,
      refetchInterval: 4 * 60_000,
      queryFn: () => vmsApi.floorplanImage(fp.owner, fp.floorplan_id).then((r) => r.url),
    })),
  });
  const stamp = fps.map((fp, i) => `${fp.floorplan_id}:${qs[i]?.data ?? ''}`).join(',');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useMemo(() => new Map(fps.map((fp, i) => [fp.floorplan_id, qs[i]?.data])), [stamp]);
}

export interface HomeMapInfo {
  homeId: string;
  api: string;
  label: string;
  geo: HomeGeo | null;
  placements: ApiPlacement[];
  canWrite: boolean;
}

export function useHomeMapInfo(): { homes: HomeMapInfo[]; loading: boolean } {
  const { results } = useDevices();
  const geo = useQueries({
    queries: results.map((r) => ({
      queryKey: ['home-geo', r.home.home_id],
      queryFn: () => vmsApi.homeGeo(r.home.endpoints.api, r.home.home_id).catch(() => null),
      staleTime: 60_000,
    })),
  });
  const pl = useQueries({
    queries: results.map((r) => ({
      queryKey: ['geo-placements', r.home.home_id],
      queryFn: () => vmsApi.geoPlacements(r.home.endpoints.api, r.home.home_id),
      staleTime: 10_000,
      retry: false,
    })),
  });
  const stamp = results.map((r, i) => `${r.home.home_id}:${geo[i]?.dataUpdatedAt}:${pl[i]?.dataUpdatedAt}`).join(',');
  const homes = useMemo(
    () =>
      results.map((r, i) => ({
        homeId: r.home.home_id,
        api: r.home.endpoints.api,
        label: r.home.display_name || r.home.home_id,
        geo: geo[i]?.data ?? null,
        placements: pl[i]?.data?.data ?? [],
        canWrite: !!pl[i]?.data?.can_write,
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [stamp],
  );
  return { homes, loading: geo.some((q) => q.isLoading) || pl.some((q) => q.isLoading) };
}

/** 組織場域（有座標的才上地圖）。 */
export function useOrgSites() {
  const { org, orgs, scope } = useScope();
  const os = scope.startsWith('home:') ? [] : org ? [org] : orgs;
  const qs = useQueries({
    queries: os.map((o) => ({ queryKey: ['sites', o.org_id], queryFn: () => orgApi.sites(o), staleTime: 60_000, retry: false })),
  });
  const stamp = os.map((o, i) => `${o.org_id}:${qs[i]?.dataUpdatedAt}`).join(',');
  return useMemo(
    () => os.flatMap((o, i) => (qs[i]?.data ?? []).map((s: Site) => ({ ...s, orgName: o.display_name }))),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [stamp],
  );
}

export function usePrefs(api: string | undefined) {
  return useQuery({ queryKey: ['vms-prefs', api], enabled: !!api, queryFn: () => vmsApi.prefs(api!).catch((): UserPrefs => ({})), staleTime: 5 * 60_000 });
}
